import { useMemo } from 'react';

import useSWRInfinite from 'swr/infinite';

import { get } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

import { SocialProfileType } from '@/types/socialProfileType';

import { STORIES } from '@/lib/routes/apiEndpoints';
import { INSTAGRAM } from '@/lib/socialProfile';

type UseGroupStoriesProps = {
    groupId?: number | string;
    activeGroupProfiles?: SocialProfileType[];
    selectedProfileIds?: (string | number)[];
    limit?: number;
    params?: {
        start?: string;
        end?: string;
        filter_labels?: string[];
        filter_hashtags?: string[];
        filter_post_type?: string[];
    };
    options?: {
        enabled?: boolean;
        revalidateOnFocus?: boolean;
        revalidateOnReconnect?: boolean;
    };
};

type Story = {
    id: string;
    created: string;
    [key: string]: any;
};

type ProfileStoriesResponse = {
    profileId: string | number;
    stories: Story[];
    cursor?: string;
};

type PageData = ProfileStoriesResponse[];

const useGroupStories = ({
    groupId,
    activeGroupProfiles = [],
    selectedProfileIds = [],
    limit = 20,
    params = {},
    options = {}
}: UseGroupStoriesProps) => {
    const { enabled = true, revalidateOnFocus = false, revalidateOnReconnect = false } = options;

    // Get Instagram profiles from selected social profiles
    const instagramProfiles = useMemo(() => {
        if (!activeGroupProfiles || activeGroupProfiles.length === 0) return [];

        return activeGroupProfiles.filter(
            (profile) =>
                profile.social_network_type === INSTAGRAM &&
                profile.active &&
                (selectedProfileIds.length === 0 || selectedProfileIds.includes(profile.id))
        );
    }, [activeGroupProfiles, selectedProfileIds]);

    // Build query params helper
    const buildQueryParams = (cursor?: string) => {
        const queryParams = new URLSearchParams();
        queryParams.append('period', 'custom');
        queryParams.append('with_labels', 'true');
        queryParams.append('per_page', limit.toString());

        if (params.start) queryParams.append('start', params.start);
        if (params.end) queryParams.append('end', params.end);
        if (cursor) queryParams.append('cursor', cursor);

        const labels = params.filter_labels || [];
        labels.forEach((label) => {
            queryParams.append('filter_labels[]', label);
        });

        const hashtags = params.filter_hashtags || [];
        hashtags.forEach((hashtag) => {
            queryParams.append('filter_hashtags[]', hashtag);
        });

        const postTypes = params.filter_post_type || [];
        postTypes.forEach((type) => {
            queryParams.append('filter_post_type[]', type);
        });

        return queryParams;
    };

    // SWR Infinite getKey function
    const getKey = (pageIndex: number, previousPageData: PageData | null) => {
        // If no groupId or no profiles, don't fetch
        if (!enabled || !groupId || instagramProfiles.length === 0) return null;

        // If we've reached the end (no more data), stop fetching
        if (previousPageData && !previousPageData.some((profile) => profile.cursor)) return null;

        // Build the key for this page
        const profileIds = instagramProfiles.map((p) => p.id).join(',');
        const cursors =
            pageIndex === 0
                ? ''
                : previousPageData?.map((p) => `${p.profileId}:${p.cursor || ''}`).join('|') || '';

        return [
            'group-stories',
            groupId,
            profileIds,
            params.start,
            params.end,
            params.filter_labels?.join(','),
            params.filter_hashtags?.join(','),
            params.filter_post_type?.join(','),
            limit,
            cursors,
            pageIndex
        ];
    };

    // SWR Infinite fetcher function
    const fetcher = async (key: any[]): Promise<PageData> => {
        const cursorsStr = key[9] as string;
        const cursorsMap = new Map<string | number, string>();

        // Parse cursors from previous page
        if (cursorsStr) {
            cursorsStr.split('|').forEach((entry) => {
                const [profileId, cursor] = entry.split(':');
                if (profileId && cursor) {
                    cursorsMap.set(profileId, cursor);
                }
            });
        }

        // Fetch stories for all profiles in parallel
        const storiesPromises = instagramProfiles.map(async (profile) => {
            const endpoint = injectEndpointUrlParams(STORIES, { SPID: profile.id });
            const cursor = cursorsMap.get(profile.id.toString());
            const queryParams = buildQueryParams(cursor);
            const fullUrl = `${endpoint}?${queryParams.toString()}`;

            const res = await get(fullUrl);
            const data = res.data;

            return {
                profileId: profile.id,
                stories: data?.stories || [],
                cursor: data?.cursor
            };
        });

        return Promise.all(storiesPromises);
    };

    // Use SWR Infinite
    const { data, error, isLoading, isValidating, size, setSize, mutate } =
        useSWRInfinite<PageData>(getKey, fetcher, {
            revalidateOnFocus,
            revalidateOnReconnect,
            revalidateFirstPage: false,
            parallel: false
        });

    // Flatten and sort all stories from all pages and profiles
    const allStories = useMemo(() => {
        if (!data) return [];

        const flattenedStories = data.flatMap((page) => page.flatMap((profile) => profile.stories));

        // Sort by creation date (most recent first)
        flattenedStories.sort(
            (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
        );

        return flattenedStories;
    }, [data]);

    // Check if there are more stories to load
    const hasMoreStories = useMemo(() => {
        if (!data || data.length === 0) return false;
        const lastPage = data[data.length - 1];
        return lastPage.some((profile) => Boolean(profile.cursor));
    }, [data]);

    // Load more function
    const loadMoreStories = () => {
        if (!isValidating && hasMoreStories) {
            setSize(size + 1);
        }
    };

    // Mutate stories function
    const mutateStories = () => {
        mutate();
    };

    return {
        dataStories: allStories.length > 0 ? [{ medias: allStories, stories: allStories }] : [],
        isLoadingInitialData: isLoading,
        isLoadingMore: isValidating && size > 1,
        isLoadMore: hasMoreStories,
        loadMoreStories,
        mutateStories,
        error
    };
};

export { useGroupStories };
